import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { ChevronUp, ChevronDown, RotateCcw } from 'lucide-react';
import { serviceCallsAPI, invoiceFoldersAPI, quickbooksAPI } from '@shared/apiClient';
import { getErrorMessage } from '@shared/utils';
import ReturnToTechModal from '../../components/ReturnToTechModal';
import { useShell } from '../../shell/ShellContext';
import { downloadInvoicePdfForServiceCall, printInvoiceForServiceCall } from '../../utils/invoiceActions';
import { generateServiceCallPDF, printServiceCallPDF, getServiceCallPDFBase64 } from '../../utils/pdfGenerator';
import MailboxSidebar from './MailboxSidebar';
import InvoiceMessageList from './InvoiceMessageList';
import InvoiceDetailPane from './InvoiceDetailPane';
import InvoiceActionBar from './InvoiceActionBar';
import InvoiceStatusBar from './InvoiceStatusBar';
import CreateFolderModal from './CreateFolderModal';
import { confirmFinalizeWithWarnings, getFinalizeWarnings } from './finalizeWarnings';

const FOLDER_PAGE_SIZE = 100;

export default function InvoicingWorkspace() {
  const queryClient = useQueryClient();
  const { setInvoicingProgress } = useShell();

  const [activeMailbox, setActiveMailbox] = useState('customers');
  const [activeFolder, setActiveFolder] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [sessionFinalized, setSessionFinalized] = useState(0);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [createFolderMailbox, setCreateFolderMailbox] = useState(null);
  const [editingFolder, setEditingFolder] = useState(null);
  const [folderPage, setFolderPage] = useState(1);
  const [loadedItems, setLoadedItems] = useState([]);
  const [folderPagination, setFolderPagination] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const { data: qboStatusData } = useQuery('quickbooksStatus', () => quickbooksAPI.getStatus(), {
    staleTime: 60000,
  });
  const qboConnected = qboStatusData?.data?.connected;

  const { data: folders = [] } = useQuery(
    ['invoiceFolders', activeMailbox],
    () => invoiceFoldersAPI.getFolders(activeMailbox).then((res) => res.data.folders)
  );

  // Default to the mailbox's Inbox once folders load, or when the mailbox changes.
  useEffect(() => {
    if (folders.length === 0) return;
    if (activeFolder && activeFolder.mailbox === activeMailbox) return;
    const inbox = folders.find((f) => f.systemKey === 'inbox') || folders[0];
    setActiveFolder(inbox);
    setSelectedId(null);
    setFolderPage(1);
    setLoadedItems([]);
    setSelectedIds(new Set());
  }, [folders, activeMailbox, activeFolder]);

  const manualFolders = useMemo(() => folders.filter((f) => f.kind === 'manual'), [folders]);

  const { data: folderItemsData, isLoading: itemsLoading, isFetching: itemsFetching } = useQuery(
    ['invoiceFolderItems', activeFolder?.id, folderPage],
    () => invoiceFoldersAPI.getFolderServiceCalls(activeFolder.id, {
      limit: FOLDER_PAGE_SIZE,
      page: folderPage
    }),
    { enabled: !!activeFolder?.id, refetchInterval: 60000, keepPreviousData: true }
  );

  useEffect(() => {
    setFolderPage(1);
    setLoadedItems([]);
    setSelectedIds(new Set());
    setSelectedId(null);
  }, [activeFolder?.id]);

  useEffect(() => {
    const pageItems = folderItemsData?.data?.serviceCalls || [];
    const pagination = folderItemsData?.data?.pagination || null;
    setFolderPagination(pagination);
    if (folderPage === 1) {
      setLoadedItems(pageItems);
    } else if (pageItems.length) {
      setLoadedItems((prev) => {
        const seen = new Set(prev.map((c) => c.id));
        return [...prev, ...pageItems.filter((c) => !seen.has(c.id))];
      });
    }
  }, [folderItemsData, folderPage]);

  const items = loadedItems;

  const selectedSummary = useMemo(
    () => items.find((c) => c.id === selectedId) || null,
    [items, selectedId]
  );

  useEffect(() => {
    if (items.length > 0 && !selectedId) {
      setSelectedId(items[0].id);
    }
    if (selectedId && items.length > 0 && !items.some((c) => c.id === selectedId)) {
      setSelectedId(items[0]?.id || null);
    }
  }, [items, selectedId]);

  const { data: detailData, isLoading: detailLoading } = useQuery(
    ['serviceCall', selectedId],
    () => serviceCallsAPI.getServiceCall(selectedId),
    { enabled: !!selectedId }
  );

  const fullCall = detailData?.data?.serviceCall || selectedSummary;

  useEffect(() => {
    setInvoicingProgress({
      finalized: sessionFinalized,
      remaining: activeFolder?.systemKey === 'inbox' ? (folderPagination?.total ?? items.length) : null,
    });
    return () => setInvoicingProgress(null);
  }, [sessionFinalized, items.length, folderPagination?.total, activeFolder?.systemKey, setInvoicingProgress]);

  const invalidateListsForCall = () => {
    queryClient.invalidateQueries(['invoiceFolderItems']);
    queryClient.invalidateQueries(['invoiceFolderCount']);
    queryClient.invalidateQueries(['serviceCall', selectedId]);
  };

  const selectByOffset = (offset) => {
    const idx = items.findIndex((c) => c.id === selectedId);
    const next = items[idx + offset];
    if (next) setSelectedId(next.id);
  };

  useEffect(() => {
    // Require Alt so plain arrow keys stay free for scrolling the detail
    // pane / page — the old bare-arrow shortcut hijacked scrolling anywhere
    // on the screen, since most of the detail pane isn't a form field.
    const onKeyDown = (e) => {
      if (!e.altKey) return;
      if (e.target.matches('input, textarea, select')) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); selectByOffset(1); }
      if (e.key === 'ArrowUp') { e.preventDefault(); selectByOffset(-1); }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  });

  const starMutation = useMutation(
    ({ id, isStarred }) => serviceCallsAPI.setStar(id, isStarred),
    {
      onSuccess: invalidateListsForCall,
      onError: (error) => toast.error(getErrorMessage(error)),
    }
  );

  const checklistMutation = useMutation(
    ({ id, updates }) => serviceCallsAPI.updateChecklist(id, updates),
    {
      onSuccess: invalidateListsForCall,
      onError: (error) => toast.error(getErrorMessage(error)),
    }
  );

  const finalizeMutation = useMutation(
    (id) => serviceCallsAPI.finalizeServiceCall(id),
    {
      onSuccess: () => {
        toast.success('Finalized — moving to Completed');
        setSessionFinalized((n) => n + 1);
        queryClient.invalidateQueries(['invoiceFolderItems']);
        queryClient.invalidateQueries(['invoiceFolderCount']);
        queryClient.invalidateQueries('toFinalizeCount');
        selectByOffset(1);
      },
      onError: (error) => toast.error(getErrorMessage(error)),
    }
  );

  const sendFranklinMutation = useMutation(
    async () => {
      const { base64, filename } = await getServiceCallPDFBase64(fullCall);
      return serviceCallsAPI.sendFranklinCompletion(fullCall.id, { pdfBase64: base64, filename });
    },
    {
      onSuccess: () => {
        toast.success('Sent to Franklin');
        invalidateListsForCall();
      },
      onError: (error) => toast.error(getErrorMessage(error)),
    }
  );

  const moveMutation = useMutation(
    ({ callId, folderId }) => invoiceFoldersAPI.assignToFolder(folderId, callId),
    {
      onSuccess: () => {
        toast.success('Moved to folder');
        invalidateListsForCall();
      },
      onError: (error) => toast.error(getErrorMessage(error)),
    }
  );

  const markPrinted = (id) => {
    checklistMutation.mutate({ id, updates: { printed: true } });
  };

  const handlePrint = async () => {
    if (!fullCall) return;
    try {
      if (fullCall.isCspire) {
        await printInvoiceForServiceCall(fullCall.id);
      } else {
        await printServiceCallPDF(fullCall);
      }
      markPrinted(fullCall.id);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleDownload = async () => {
    if (!fullCall) return;
    try {
      if (fullCall.isCspire) {
        await downloadInvoicePdfForServiceCall(fullCall.id, fullCall.invoiceNumber);
      } else {
        await generateServiceCallPDF(fullCall);
      }
      toast.success('PDF downloaded');
      markPrinted(fullCall.id);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleFinalize = () => {
    if (!fullCall) return;
    const warnings = getFinalizeWarnings(fullCall, activeMailbox);
    if (!confirmFinalizeWithWarnings(warnings)) return;
    finalizeMutation.mutate(fullCall.id);
  };

  const handleSend = () => {
    if (!fullCall) return;
    if (activeMailbox === 'franklin') {
      sendFranklinMutation.mutate();
    }
  };

  const handleToggleManualStep = (key, value) => {
    if (!fullCall) return;
    checklistMutation.mutate({ id: fullCall.id, updates: { [key]: value } });
  };

  const toggleSelectedId = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllVisible = (ids) => {
    setSelectedIds((prev) => {
      const allSelected = ids.length > 0 && ids.every((id) => prev.has(id));
      if (allSelected) return new Set();
      return new Set(ids);
    });
  };

  const runBulkAction = async (action) => {
    const ids = [...selectedIds];
    if (!ids.length) return;

    if (action === 'quickbooks' && activeMailbox === 'customers' && qboConnected) {
      setBulkBusy(true);
      try {
        const res = await quickbooksAPI.pushBulk(ids);
        const results = res.data?.results || [];
        const pushed = results.filter((r) => r.ok && !r.skipped).length;
        const skipped = results.filter((r) => r.ok && r.skipped).length;
        const failed = results.filter((r) => !r.ok);
        if (pushed) toast.success(`Pushed ${pushed} to QuickBooks${skipped ? `, ${skipped} already synced` : ''}`);
        else if (skipped) toast(`${skipped} already synced to QuickBooks`);
        if (failed.length) {
          toast.error(`${failed.length} failed: ${failed[0].error || 'QuickBooks error'}`);
        }
        setSelectedIds(new Set());
        queryClient.invalidateQueries(['invoiceFolderItems']);
        queryClient.invalidateQueries(['invoiceFolderCount']);
      } catch (error) {
        toast.error(getErrorMessage(error));
      } finally {
        setBulkBusy(false);
      }
      return;
    }

    setBulkBusy(true);
    let success = 0;
    try {
      for (const id of ids) {
        const call = items.find((c) => c.id === id);
        if (action === 'quickbooks') {
          await serviceCallsAPI.updateChecklist(id, { addedToQuickbooks: true });
          success += 1;
        } else if (action === 'finalize') {
          if (call?.isFinalized) continue;
          if (success === 0) {
            const warnings = getFinalizeWarnings(call || { id }, activeMailbox);
            if (!confirmFinalizeWithWarnings(warnings)) {
              setBulkBusy(false);
              return;
            }
          }
          await serviceCallsAPI.finalizeServiceCall(id);
          success += 1;
          setSessionFinalized((n) => n + 1);
        }
      }
      toast.success(
        action === 'quickbooks'
          ? `Marked ${success} item(s) as added to QuickBooks`
          : `Finalized ${success} item(s)`
      );
      setSelectedIds(new Set());
      queryClient.invalidateQueries(['invoiceFolderItems']);
      queryClient.invalidateQueries(['invoiceFolderCount']);
      queryClient.invalidateQueries('toFinalizeCount');
    } catch (error) {
      toast.error(getErrorMessage(error));
      queryClient.invalidateQueries(['invoiceFolderItems']);
    } finally {
      setBulkBusy(false);
    }
  };

  const hasMore = folderPagination?.hasNext;

  return (
    <div className="h-full flex min-h-0">
      <MailboxSidebar
        activeMailbox={activeMailbox}
        activeFolderId={activeFolder?.id}
        onSelectMailbox={(mailbox) => {
          setActiveMailbox(mailbox);
          if (mailbox !== activeFolder?.mailbox) setActiveFolder(null);
        }}
        onSelectFolder={(mailbox, folder) => {
          setActiveMailbox(mailbox);
          setActiveFolder(folder);
          setSelectedId(null);
        }}
        onRequestCreateFolder={(mailbox) => setCreateFolderMailbox(mailbox)}
        onRequestEditFolder={(folder) => setEditingFolder(folder)}
      />

      <InvoiceMessageList
        mailbox={activeMailbox}
        folderName={activeFolder?.name || ''}
        items={items}
        isLoading={itemsLoading && folderPage === 1}
        selectedId={selectedId}
        onSelect={setSelectedId}
        onToggleStar={(id, isStarred) => starMutation.mutate({ id, isStarred })}
        manualFolders={manualFolders}
        currentFolderId={activeFolder?.kind === 'manual' ? activeFolder.id : null}
        onMoveToFolder={(callId, folderId) => moveMutation.mutate({ callId, folderId })}
        selectedIds={selectedIds}
        onToggleSelect={toggleSelectedId}
        onToggleSelectAll={toggleSelectAllVisible}
        onBulkMarkQuickBooks={() => runBulkAction('quickbooks')}
        qboConnected={qboConnected}
        onBulkFinalize={() => runBulkAction('finalize')}
        bulkBusy={bulkBusy}
        totalCount={folderPagination?.total}
        hasMore={hasMore}
        onLoadMore={() => setFolderPage((p) => p + 1)}
        loadingMore={itemsFetching && folderPage > 1}
      />

      <div className="flex-1 flex flex-col min-w-0 min-h-0 bg-surface-secondary">
        {!fullCall ? (
          <div className="flex-1 flex items-center justify-center text-sm text-gray-500">
            Select an item to review
          </div>
        ) : detailLoading && !selectedSummary ? (
          <div className="flex-1 flex items-center justify-center text-sm text-gray-500">Loading...</div>
        ) : (
          <>
            <div className="flex-shrink-0 px-4 py-1.5 border-b border-surface-border bg-surface flex items-center justify-end gap-1">
              <button type="button" className="btn-sm btn-ghost" onClick={() => setShowReturnModal(true)} title="Return to Tech">
                <RotateCcw className="h-3.5 w-3.5" />
              </button>
              <button type="button" className="btn-sm btn-ghost" onClick={() => selectByOffset(-1)}>
                <ChevronUp className="h-4 w-4" />
              </button>
              <button type="button" className="btn-sm btn-ghost" onClick={() => selectByOffset(1)}>
                <ChevronDown className="h-4 w-4" />
              </button>
            </div>

            <InvoiceDetailPane
              call={fullCall}
              mailbox={activeMailbox}
              onCspirePrinted={() => markPrinted(fullCall.id)}
            />

            <InvoiceStatusBar
              mailbox={activeMailbox}
              call={fullCall}
              onToggleManualStep={handleToggleManualStep}
            />
            <InvoiceActionBar
              mailbox={activeMailbox}
              call={fullCall}
              onPrint={handlePrint}
              onDownload={handleDownload}
              onFinalize={handleFinalize}
              onSend={handleSend}
              isFinalizing={finalizeMutation.isLoading}
              isSending={sendFranklinMutation.isLoading}
            />
          </>
        )}
      </div>

      <ReturnToTechModal
        serviceCall={fullCall}
        isOpen={showReturnModal}
        onClose={() => setShowReturnModal(false)}
        onConfirm={async (reason) => {
          if (!fullCall || !reason) return;
          try {
            await serviceCallsAPI.updateServiceCall(fullCall.id, {
              needsTechInfo: true,
              techInfoRequestReason: reason,
              techInfoRequestedAt: new Date().toISOString(),
              techInfoUpdatedAt: null,
            });
            toast.success('Request sent to technician');
            invalidateListsForCall();
            setShowReturnModal(false);
          } catch (error) {
            toast.error(getErrorMessage(error));
          }
        }}
      />

      <CreateFolderModal
        mailbox={createFolderMailbox || editingFolder?.mailbox}
        folder={editingFolder}
        isOpen={!!createFolderMailbox || !!editingFolder}
        onClose={() => {
          setCreateFolderMailbox(null);
          setEditingFolder(null);
        }}
      />
    </div>
  );
}
